/**
 * GoogleMapProvider - Google Maps tiles via Leaflet + GoogleMutant
 *
 * Uses Leaflet API with GoogleMutant plugin to display Google Maps tiles.
 * This provides a unified API (L.marker, L.popup) regardless of tile provider.
 */
import { MapProvider } from './MapProvider.js';
import { FullWindowControl } from '../controls/FullWindowControl.js';
import { MapTypeControl } from '../controls/MapTypeControl.js';
import { createMarkerIcon } from '../MarkerUtils.js';

export class GoogleMapProvider extends MapProvider {
	constructor(elementId, options = {}) {
		super(elementId, options);
		this.mapType = 'google';
		this.googleLayer = null;
		this.googleLayers = {}; // Cache for different map type layers
		this.currentMapType = null;
		this.oms = null; // OverlappingMarkerSpiderfier instance

		// Normalize styles - ensure it's undefined if not a valid array
		if (!Array.isArray(this.options.styles) || this.options.styles.length === 0) {
			delete this.options.styles;
		}
	}

	/**
	 * Initialize Leaflet map with Google Maps tiles via GoogleMutant
	 */
	init() {

		// Check if Leaflet is loaded
		if (!window.L) {
			return false;
		}

		// Check if GoogleMutant plugin is loaded
		if (!L.gridLayer.googleMutant) {
			return false;
		}

		// Check if Google Maps API is loaded (required by GoogleMutant)
		if (!window.google || !google.maps) {
			return false;
		}


		// Get map element
		const mapElement = document.getElementById(this.elementId);

		if (!mapElement) {
			return false;
		}

		// Map type conversion (roadmap, satellite, terrain, hybrid)
		let googleMapType = 'roadmap';
		if (this.options.maptype) {
			const mapTypeLower = this.options.maptype.toLowerCase();
			if (['roadmap', 'satellite', 'terrain', 'hybrid'].includes(mapTypeLower)) {
				googleMapType = mapTypeLower;
			}
		}

		// Create Leaflet map
		this.map = L.map(this.elementId, {
			center: [
				parseFloat(this.options.latitude) || 0,
				parseFloat(this.options.longitude) || 0
			],
			zoom: parseInt(this.options.zoom) || 12,
			scrollWheelZoom: this.options.scrollwheel !== false,
			zoomControl: this.options.zoomControl !== false,
			attributionControl: false, // Remove attribution control
			...this.options.mapOptions
		});

		// Create initial Google Maps layer
		this.googleLayer = this.createLayer(googleMapType);
		this.googleLayer.addTo(this.map);

		// Store current map type
		this.currentMapType = googleMapType;

		// Setup tile load handler to hide loading spinner
		this.setupTileLoadHandler(this.googleLayer);

		// Add body class to indicate Google Maps is loaded
		document.body.classList.add('gd-map-loaded-google');

		// Apply z-index fixes to prevent tiles from covering markers
		this.applyZIndexFixes();

		// Initialize Map Type control
		this.mapTypeControl = new MapTypeControl(this, mapElement, {
			initialType: googleMapType,
			types: ['roadmap', 'satellite', 'terrain', 'hybrid']
		});
		this.mapTypeControl.init();

		// Initialize OverlappingMarkerSpiderfier if available
		if (window.OverlappingMarkerSpiderfier) {
			this.oms = new window.OverlappingMarkerSpiderfier(this.map, {
				markersWontMove: true,
				markersWontHide: true,
				basicFormatEvents: true,
				keepSpiderfied: false,
				nearbyDistance: 20
			});
		}

		// Initialize Full Window control
		const fullWindowContainer = mapElement.parentElement;
		if (fullWindowContainer) {
			this.fullWindowControl = new FullWindowControl(this.map, mapElement, fullWindowContainer, {
				hide: this.options.hideExpandMap || false
			});
			this.fullWindowControl.init();
		}

		return true;
	}

	/**
	 * Apply CSS z-index fixes for proper marker layering
	 */
	applyZIndexFixes() {
		// Create style element if it doesn't exist
		if (!document.getElementById('geodir-googlemutant-zindex-fix')) {
			const style = document.createElement('style');
			style.id = 'geodir-googlemutant-zindex-fix';
			style.textContent = `
				/* Ensure Google tiles stay behind markers */
				.leaflet-google-mutant {
					z-index: 0 !important;
				}
				/* Ensure markers appear above tiles */
				.leaflet-map-pane {
					z-index: 100 !important;
				}
				.leaflet-marker-pane {
					z-index: 600 !important;
				}
				.leaflet-popup-pane {
					z-index: 700 !important;
				}
			`;
			document.head.appendChild(style);
		}
	}

	/**
	 * Create a marker using Leaflet API
	 */
	createMarker(options) {
		const lat = parseFloat(options.lat || options.latitude) || 0;
		const lng = parseFloat(options.lng || options.longitude) || 0;

		const markerOptions = {
			draggable: options.draggable !== false,
			title: options.title || '',
			data: options.data // Store marker data for later retrieval
		};

		// Create icon using shared utility
		const icon = createMarkerIcon(options);
		if (icon) {
			markerOptions.icon = icon;
		}

		// Create Leaflet marker
		const marker = L.marker([lat, lng], markerOptions).addTo(this.map);

		// Add to OverlappingMarkerSpiderfier if available
		if (this.oms) {
			this.oms.addMarker(marker);
		}

		this.markers.push(marker);
		return marker;
	}

	/**
	 * Get center of map
	 */
	getCenter() {
		if (!this.map) return null;
		const center = this.map.getCenter();
		return {
			lat: center.lat,
			lng: center.lng
		};
	}

	/**
	 * Set center of map
	 */
	setCenter(lat, lng) {
		if (!this.map) return;
		this.map.setView([lat, lng]);
	}

	/**
	 * Pan to location
	 */
	panTo(lat, lng) {
		if (!this.map) return;
		this.map.panTo([lat, lng]);
	}

	/**
	 * Get zoom level
	 */
	getZoom() {
		if (!this.map) return null;
		return this.map.getZoom();
	}

	/**
	 * Set zoom level
	 */
	setZoom(zoom) {
		if (!this.map) return;
		this.map.setZoom(zoom);
	}

	/**
	 * Create a GoogleMutant layer for specified type
	 */
	createLayer(type) {
		// Return cached layer if exists
		if (this.googleLayers[type]) {
			return this.googleLayers[type];
		}

		// Create new layer
		const config = {
			type: type,
			maxZoom: 21
		};

		// Only add styles to roadmap (already normalized in constructor)
		if (type === 'roadmap' && this.options.styles) {
			config.styles = this.options.styles;
		}

		this.googleLayers[type] = L.gridLayer.googleMutant(config);
		return this.googleLayers[type];
	}

	/**
	 * Set map type (roadmap, satellite, terrain, hybrid)
	 */
	setMapType(type) {
		if (this.currentMapType === type) {
			return true;
		}

		// Show loading spinner during map type switch
		this.showLoading();

		// Remove current layer
		if (this.googleLayer && this.map.hasLayer(this.googleLayer)) {
			this.map.removeLayer(this.googleLayer);
		}

		// Get or create new layer
		this.googleLayer = this.createLayer(type);
		this.googleLayer.addTo(this.map);

		// Setup tile load handler to hide loading after new tiles load
		this.setupTileLoadHandler(this.googleLayer);

		this.currentMapType = type;
		return true;
	}

	/**
	 * Get current map type
	 */
	getMapType() {
		return this.currentMapType;
	}

	/**
	 * Forward geocode using Google Maps Geocoder
	 */
	geocode(request) {
		return new Promise((resolve, reject) => {
			if (!google.maps || !google.maps.Geocoder) {
				reject(new Error('Google Maps Geocoder not available'));
				return;
			}

			const geocoder = new google.maps.Geocoder();
			const geocodeRequest = typeof request === 'string'
				? { address: request }
				: request;

			geocoder.geocode(geocodeRequest, (results, status) => {
				if (status === google.maps.GeocoderStatus.OK) {
					resolve(results);
				} else {
					reject(new Error(`Geocoding failed: ${status}`));
				}
			});
		});
	}

	/**
	 * Reverse geocode using Google Maps Geocoder
	 */
	reverseGeocode(lat, lng) {
		return new Promise((resolve, reject) => {
			if (!google.maps || !google.maps.Geocoder) {
				reject(new Error('Google Maps Geocoder not available'));
				return;
			}

			const geocoder = new google.maps.Geocoder();
			geocoder.geocode({
				latLng: new google.maps.LatLng(lat, lng)
			}, (results, status) => {
				if (status === google.maps.GeocoderStatus.OK) {
					resolve(results);
				} else {
					reject(new Error(`Reverse geocoding failed: ${status}`));
				}
			});
		});
	}

	/**
	 * Add event listener to map
	 */
	addEventListener(event, callback) {
		if (!this.map) return;

		// Map event names to Leaflet events
		const eventMap = {
			'dragend': 'dragend',
			'zoom_changed': 'zoomend',
			'click': 'click'
		};

		const leafletEvent = eventMap[event] || event;
		this.map.on(leafletEvent, callback);
	}

	/**
	 * Add marker event listener using Leaflet API
	 */
	addMarkerListener(marker, event, callback) {
		if (!marker) return;
		marker.on(event, callback);
	}

	/**
	 * Fit map bounds to show all markers
	 * @param {Array} markers - Array of Leaflet marker objects
	 */
	fitBounds(markers) {
		if (!this.map || !markers || markers.length === 0) {
			return;
		}


		// Create Leaflet LatLngBounds
		const bounds = L.latLngBounds();

		markers.forEach(marker => {
			if (marker && marker.getLatLng) {
				bounds.extend(marker.getLatLng());
			}
		});

		this.map.fitBounds(bounds);

		// If there's only one marker, set a reasonable zoom level
		if (markers.length === 1) {
			setTimeout(() => {
				if (this.map.getZoom() > 15) {
					this.map.setZoom(15);
				}
			}, 100);
		}

	}

	/**
	 * Open a popup for a marker using Leaflet API.
	 *
	 * @param {object} marker - Leaflet Marker object
	 * @param {string} content - HTML content to display
	 */
	openPopup(marker, content) {
		if (!this.map || !marker) {
			return;
		}


		// Create Leaflet popup with content
		const popup = L.popup({
			maxWidth: 300,
			className: 'geodir-marker-popup'
		}).setContent(content);

		// Bind and open popup
		marker.bindPopup(popup);
		marker.openPopup();

	}

	/**
	 * Close any open popup.
	 */
	closePopup() {
		if (this.map) {
			this.map.closePopup();
		}
	}
}
